import React, { useCallback, useEffect, useState } from "react";
import { UsersIcon, DevicePhoneMobileIcon, BoltIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import getUsersAnalytics from "../api/getUsersAnalytics";
import { Card, Kpi, KpiSkeleton, ErrorState, Empty, RefreshBtn, Segmented, RankList, useChartTheme, fmt } from "./monitoring/ui";

const PERIODS = [
  { value: "7d", label: "7 дней" },
  { value: "30d", label: "30 дней" },
  { value: "90d", label: "90 дней" },
  { value: "1y", label: "1 год" },
];

const GRANULARITY = [
  { value: "daily", label: "По дням" },
  { value: "weekly", label: "По неделям" },
  { value: "monthly", label: "По месяцам" },
];

const TYPE_LABELS = {
  individual: "Физ. лица",
  business: "Бизнес",
  dealer: "Бизнес (старый тип)",
  support: "Поддержка",
  admin: "Администраторы",
  moderator: "Модераторы",
  operator: "Операторы",
  sales_manager: "Менеджеры продаж",
};

const Funnel = ({ total, rows }) => (
  <ul className="space-y-4 p-5">
    {rows.map(({ label, count, rate, color }) => (
      <li key={label}>
        <div className="mb-1.5 flex items-baseline justify-between gap-3 text-sm">
          <span className="text-text-secondary">{label}</span>
          <span className="tabular-nums text-text-primary">
            <b>{fmt(count)}</b>
            <span className="ml-2 text-xs text-text-muted">{rate === undefined || rate === null ? "—" : `${Number(rate).toFixed(1)}%`}</span>
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-bg-tertiary">
          <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(Number(rate) || (total ? (count / total) * 100 : 0), 100)}%` }} />
        </div>
      </li>
    ))}
  </ul>
);

const AllUsersAnalytics = () => {
  const [period, setPeriod] = useState("30d");
  const [granularity, setGranularity] = useState("daily");
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);
  const chart = useChartTheme();

  const fetchAnalytics = useCallback(async () => {
    setState("loading");
    setError(null);
    try {
      setData(await getUsersAnalytics(period));
      setState("ready");
    } catch (err) {
      console.error("Error fetching users analytics:", err);
      setError(err.message);
      setState("error");
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const s = data?.summary || {};
  const total = data?.total_registered_users ?? s.total_registered ?? 0;
  const byType = Object.entries(data?.users_by_type || {})
    .map(([k, v]) => ({ label: TYPE_LABELS[k] || k, value: Number(v) || 0 }))
    .sort((a, b) => b.value - a.value);
  const series = (data?.registrations?.[granularity] || []).map((i) => ({
    label: i.date || i.day || i.week || i.month || i.period || "",
    count: i.count || i.registrations || 0,
  }));
  const registeredInPeriod = (data?.registrations?.daily || []).reduce((acc, i) => acc + (i.count || i.registrations || 0), 0);
  const forbidden = /доступ запрещ|403/i.test(error || "");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-text-secondary">Регистрации, активность и состав пользователей из базы Agroland.</p>
        <div className="flex items-center gap-2">
          <Segmented options={PERIODS} value={period} onChange={setPeriod} />
          <RefreshBtn onClick={fetchAnalytics} loading={state === "loading"} />
        </div>
      </div>

      {state === "error" ? (
        <ErrorState text={forbidden ? "Нет доступа к аналитике пользователей" : "Не удалось загрузить аналитику"} detail={error} onRetry={forbidden ? undefined : fetchAnalytics} />
      ) : !data ? (
        <>
          <KpiSkeleton />
          <div className="h-80 animate-pulse rounded-block border border-border-primary bg-bg-card" />
        </>
      ) : (
        <div className={`space-y-4 ${state === "loading" ? "opacity-60" : ""}`}>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Зарегистрировано всего" value={total} icon={UsersIcon} />
            <Kpi label="Новых за период" value={registeredInPeriod} icon={UserPlusIcon} tone="bg-emerald-500/15 text-emerald-500" hint={PERIODS.find((p) => p.value === period)?.label} />
            <Kpi label="Активны за 24 ч" value={data.logged_in_users?.last_24_hours ?? s.active_last_24h ?? 0} icon={BoltIcon} tone="bg-amber-500/15 text-amber-500" />
            <Kpi label="Устройств" value={data.total_devices ?? 0} icon={DevicePhoneMobileIcon} tone="bg-sky-500/15 text-sky-500" hint={`у ${fmt(data.unique_users_with_devices ?? 0)} пользователей`} />
          </div>

          <Card title="Регистрации" actions={<Segmented options={GRANULARITY} value={granularity} onChange={setGranularity} />}>
            {series.length ? (
              <div className="h-72 px-2 py-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={series} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                    <XAxis dataKey="label" tick={chart.axis} axisLine={false} tickLine={false} minTickGap={16} />
                    <YAxis tick={chart.axis} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip {...chart.tooltip} formatter={(v) => [fmt(v), "Регистраций"]} />
                    <Bar dataKey="count" fill="#6366F1" radius={[4, 4, 0, 0]} maxBarSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Empty>Нет регистраций за период</Empty>
            )}
          </Card>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title="Активность" subtitle="Доля от всех зарегистрированных, кто заходил">
              <Funnel
                total={total}
                rows={[
                  { label: "За 24 часа", count: s.active_last_24h, rate: s.active_rate_24h, color: "bg-emerald-500" },
                  { label: "За 7 дней", count: s.active_last_7d, rate: s.active_rate_7d, color: "bg-sky-500" },
                  { label: "За 30 дней", count: s.active_last_30d, rate: s.active_rate_30d, color: "bg-violet-500" },
                ]}
              />
            </Card>
            <Card title="Типы аккаунтов" subtitle={`${fmt(total)} пользователей`}>
              <RankList items={byType} emptyText="Нет данных" />
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllUsersAnalytics;
